import React, { useState } from 'react';
import { colors, typography, spacing } from '../styles/design-system';

interface BaseballDataDisplayProps {
  data: any;
  homeTeam: string;
  awayTeam: string;
}

const BaseballDataDisplay: React.FC<BaseballDataDisplayProps> = ({ data, homeTeam, awayTeam }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');

  // 수신 데이터 디버그 로그
  React.useEffect(() => {
    console.log('BaseballDataDisplay 수신 데이터:', data);
    console.log('데이터 키:', Object.keys(data || {}));
    console.log('선발투수:', data?.starting_pitchers);
    console.log('시즌 선수 랭킹:', data?.season_player_rankings);
    console.log('시즌 기록:', data?.season_records);
  }, [data]);

  // 데이터 섹션 추출
  const startingPitchers = data.starting_pitchers || {};
  const headToHead = data.head_to_head || {};
  const teamStats = data.team_stats || {};
  const seasonRecords = data.season_records || {};
  const seasonPlayerRankings = data.season_player_rankings || {};
  const dateTime = data.date_time;
  const venue = data?.venue;

  // 테이블 데이터를 구조화된 형식으로 파싱
  const parseTableData = (tableArray: any[]): any[] => {
    if (!tableArray || !Array.isArray(tableArray) || tableArray.length < 2) return [];

    // 제목 행이 있으면 건너뛰고 헤더와 데이터 추출
    let startIdx = 0;
    if (tableArray[0] && tableArray[0].length === 1) {
      // 첫 번째 행이 제목이면 건너뜀
      startIdx = 1;
    }
    
    if (tableArray.length <= startIdx) return [];
    
    const headers = tableArray[startIdx];
    const dataRows = tableArray.slice(startIdx + 1);
    
    if (!Array.isArray(headers)) return [];
    
    return dataRows.map((row: any[]) => {
      const rowObj: any = {};
      headers.forEach((header: string, index: number) => {
        rowObj[header] = row[index];
      });
      return rowObj;
    });
  };

  // 파싱된 테이블을 적절한 구조로 렌더링
  const renderParsedTable = (data: any, title: string): React.ReactElement | null => {
    if (!data) return null;

    let parsedData: any[] = [];

    // 다양한 데이터 형식 처리
    if (data.data && Array.isArray(data.data)) {
      parsedData = parseTableData(data.data);
    } else if (Array.isArray(data)) {
      parsedData = parseTableData(data);
    } else if (data.headers && data.data) {
      // 기존 형식 (headers와 data 분리)
      const headers = data.headers || [];
      const rows = data.data || [];
      
      if (rows.length > 0 && headers.length > 0) {
        parsedData = rows.map((row: any) => {
          const rowObj: any = {};
          headers.forEach((header: string, idx: number) => {
            rowObj[header] = Array.isArray(row) ? row[idx] : row[header];
          });
          return rowObj;
        });
      }
    }

    if (parsedData.length === 0) return null;
    
    const headers = Object.keys(parsedData[0]);
    
    return (
      <div style={styles.tableContainer}>
        {title && <h4 style={styles.tableTitle}>{title}</h4>}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {headers.map((header, idx) => (
                  <th key={idx} style={styles.th}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsedData.map((row: any, rowIdx: number) => (
                <tr key={rowIdx}>
                  {headers.map((header, cellIdx) => (
                    <td key={cellIdx} style={styles.td}>{row[header] || '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderStartingPitcherInfo = (pitcher: any): React.ReactElement | null => {
    if (!pitcher) return null;

    // 기본 정보 테이블 데이터
    const basicInfoData = [
      ['번호', pitcher.info?.number || '-'],
      ['이름', pitcher.name || '-'],
      ['소속', pitcher.info?.team || '-'],
      ['투타', pitcher.info?.throw_hit || '-'],
      ['출생', pitcher.info?.birth || '-'],
      ['신체', pitcher.info?.height_weight || '-'],
      ['데뷔', pitcher.info?.debut || '-']
    ];

    // 시즌 성적 테이블 데이터
    const seasonRecordData = [];
    if (pitcher.season_record) {
      seasonRecordData.push(['구분', '승', '패', '세이브']);
      seasonRecordData.push([
        '전체',
        pitcher.season_record.wins || '0',
        pitcher.season_record.losses || '0',
        pitcher.season_record.saves || '0'
      ]);
      
      if (pitcher.season_record.home) {
        seasonRecordData.push([
          '홈',
          pitcher.season_record.home.wins || '0',
          pitcher.season_record.home.losses || '0',
          '-'
        ]);
      }
      
      if (pitcher.season_record.away) {
        seasonRecordData.push([
          '원정',
          pitcher.season_record.away.wins || '0',
          pitcher.season_record.away.losses || '0',
          '-'
        ]);
      }
    }

    // 방어율 테이블 데이터
    const eraData = [];
    if (pitcher.era) {
      eraData.push(['구분', '방어율']);
      eraData.push(['전체', pitcher.era.total || '-']);
      
      if (pitcher.era.home !== undefined) {
        eraData.push(['홈', pitcher.era.home]);
      }
      
      if (pitcher.era.away !== undefined) {
        eraData.push(['원정', pitcher.era.away]);
      }
    }

    return (
      <div style={styles.pitcherSection}>
        <h4 style={styles.pitcherName}>
          #{pitcher.info?.number || 'N/A'} {pitcher.name}
        </h4>
        
        {/* 기본 정보 테이블 */}
        <div style={styles.tableContainer}>
          <h5 style={styles.tableTitle}>기본 정보</h5>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <tbody>
                {basicInfoData.map((row, idx) => (
                  <tr key={idx}>
                    <th style={styles.th}>{row[0]}</th>
                    <td style={styles.td}>{row[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 시즌 성적 테이블 */}
        {seasonRecordData.length > 0 && (
          <div style={styles.tableContainer}>
            <h5 style={styles.tableTitle}>시즌 성적</h5>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {seasonRecordData[0].map((header, idx) => (
                      <th key={idx} style={styles.th}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {seasonRecordData.slice(1).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} style={styles.td}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 방어율 테이블 */}
        {eraData.length > 0 && (
          <div style={styles.tableContainer}>
            <h5 style={styles.tableTitle}>방어율</h5>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {eraData[0].map((header, idx) => (
                      <th key={idx} style={styles.th}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eraData.slice(1).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} style={styles.td}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 팀별 데이터 추출
  const getTeamSpecificData = () => {
    const team = selectedTeam === 'home' ? homeTeam : awayTeam;
    const isHome = selectedTeam === 'home';

    const result: any = {
      teamName: team,
      pitcher: null,
      seasonStats: null,
      recentPitcher: null,
      monthlyStats: null,
      teamRecent: null,
      threeSeasonStats: [],
      currentSeason: [],
      headToHead: []
    };

    // 선발투수 데이터
    if (startingPitchers) {
      result.pitcher = isHome ? startingPitchers.home_pitcher : startingPitchers.away_pitcher;
      result.seasonStats = isHome ? startingPitchers.home_season_stats : startingPitchers.away_season_stats;
      result.recentPitcher = isHome ? startingPitchers.home_recent : startingPitchers.away_recent;
      result.monthlyStats = isHome ? startingPitchers.home_monthly : startingPitchers.away_monthly;
    }

    // 팀 최근 경기
    if (teamStats) {
      result.teamRecent = isHome ? teamStats.home_recent : teamStats.away_recent;

      // 3시즌 통계 - 백엔드 분류 사용
      if (teamStats.three_season && Array.isArray(teamStats.three_season)) {
        result.threeSeasonStats = teamStats.three_season.filter((stat: any) => {
          // 백엔드 분류가 있으면 사용
          if (stat.team_type !== undefined) {
            return stat.team_type === selectedTeam || stat.team_type === null;
          }
          // 텍스트 기반 분류로 폴백
          const desc = stat.description || '';
          const isTeamStat = isHome ?
            (desc.includes('[홈팀]') || desc.includes(homeTeam)) :
            (desc.includes('[원정팀]') || desc.includes(awayTeam));
          const isNeutral = !desc.includes('[홈팀]') && !desc.includes('[원정팀]') &&
                           !desc.includes(homeTeam) && !desc.includes(awayTeam);
          return isTeamStat || isNeutral;
        });
      }
    }

    // 시즌 기록 - 백엔드 분류 사용
    if (seasonRecords && seasonRecords.current && Array.isArray(seasonRecords.current)) {
      result.currentSeason = seasonRecords.current.filter((record: any) => {
        // 백엔드 분류가 있으면 사용
        if (record.team_type !== undefined) {
          return record.team_type === selectedTeam || record.team_type === null;
        }
        // 텍스트 기반 분류로 폴백
        const desc = record.description || '';
        const isTeamRecord = isHome ? 
          (desc.includes('[홈팀]') || desc.includes(homeTeam)) :
          (desc.includes('[원정팀]') || desc.includes(awayTeam));
        const isNeutral = !desc.includes('[홈팀]') && !desc.includes('[원정팀]') && 
                         !desc.includes(homeTeam) && !desc.includes(awayTeam);
        return isTeamRecord || isNeutral;
      });
    }

    // 상대전적 - 백엔드 분류 사용
    if (headToHead && headToHead.records && Array.isArray(headToHead.records)) {
      result.headToHead = headToHead.records.filter((record: any) => {
        // 백엔드 분류가 있으면 사용
        if (record.team_type !== undefined && record.record_type !== undefined) {
          // versus 기록의 경우 team_type이 해당 팀을 나타냄
          if (record.record_type === 'versus') {
            return record.team_type === selectedTeam || record.team_type === null;
          }
          // own 기록의 경우 팀 기준으로 표시
          if (record.record_type === 'own') {
            return record.team_type === selectedTeam || record.team_type === null;
          }
          // 기타 기록은 모두 표시
          return true;
        }

        // 텍스트 기반 분류로 폴백
        const desc = record.description || '';
        const isAgainstRecord = desc.includes('과의') || desc.includes('상대전적');
        
        if (isAgainstRecord) {
          if (desc.includes(`[${isHome ? '원정팀' : '홈팀'}]과의`) || 
              desc.includes(`${isHome ? awayTeam : homeTeam}과의`)) {
            return true;
          }
          if (!desc.includes('[홈팀]') && !desc.includes('[원정팀]') && 
              !desc.includes(homeTeam) && !desc.includes(awayTeam)) {
            return true;
          }
          return false;
        } else {
          return true;
        }
      });
    }

    return result;
  };

  const teamData = getTeamSpecificData();

  return (
    <div style={styles.container}>
      {/* Match Info Header */}
      {dateTime && (
        <div style={styles.matchInfoHeader}>
          <h3 style={styles.matchTitle}>
            {homeTeam} vs {awayTeam}
          </h3>
          <div style={styles.matchDetails}>
            <span>{dateTime}</span>
            {venue && <span> | {venue}</span>}
          </div>
        </div>
      )}

      {/* Team Selection Buttons */}
      <div style={styles.teamButtons}>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'home' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('home')}
        >
          홈팀: {homeTeam}
        </button>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'away' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('away')}
        >
          원정팀: {awayTeam}
        </button>
      </div>

      {/* Team Content */}
      <div style={styles.content}>
        <h3 style={styles.teamNameTitle}>
          {teamData.teamName} 상세 데이터
        </h3>

        {/* Starting Pitcher Section */}
        {teamData.pitcher && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>선발투수</h3>
            <div style={styles.pitcherGrid}>
              {renderStartingPitcherInfo(teamData.pitcher)}
            </div>
          </div>
        )}

        {/* Team Stats Section */}
        {(teamData.teamRecent || teamData.threeSeasonStats.length > 0) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>팀 통계</h3>
            {renderParsedTable(teamData.teamRecent, '최근 경기 전적')}
            {teamData.threeSeasonStats.map((stat: any, idx: number) => (
              <div key={idx}>
                {renderParsedTable(stat, stat.description || '3시즌 통계')}
              </div>
            ))}
          </div>
        )}

        {/* Season Records Section */}
        {teamData.currentSeason.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>시즌 기록</h3>
            {teamData.currentSeason.map((record: any, idx: number) => (
              <div key={idx}>
                {renderParsedTable(record, record.description || '시즌 전적')}
              </div>
            ))}
          </div>
        )}

        {/* Head to Head Section */}
        {teamData.headToHead.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>상대전적</h3>
            {teamData.headToHead.map((record: any, idx: number) => (
              <div key={idx}>
                {renderParsedTable(record, record.description || '상대전적')}
              </div>
            ))}
          </div>
        )}

        {/* Head to Head Stats if exists */}
        {headToHead && headToHead.stats && headToHead.stats.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>상대전적 통계</h3>
            {headToHead.stats.filter((stat: any) => {
              const isHome = selectedTeam === 'home';

              // 백엔드 분류가 있으면 사용
              if (stat.team_type !== undefined) {
                return stat.team_type === selectedTeam || stat.team_type === null;
              }

              // 텍스트 기반 분류로 폴백
              const desc = stat.description || '';
              const isAgainstStat = desc.includes('과의') || desc.includes('상대전적 통계');
              
              if (isAgainstStat) {
                if (desc.includes(`[${isHome ? '홈팀' : '원정팀'}]`) || 
                    desc.includes(isHome ? homeTeam : awayTeam)) {
                  return true;
                }
                if (!desc.includes('[홈팀]') && !desc.includes('[원정팀]') && 
                    !desc.includes(homeTeam) && !desc.includes(awayTeam)) {
                  return true;
                }
                return false;
              }
              
              const isTeamStat = isHome ? 
                (desc.includes('[홈팀]') || desc.includes(homeTeam)) :
                (desc.includes('[원정팀]') || desc.includes(awayTeam));
              const isNeutral = !desc.includes('[홈팀]') && !desc.includes('[원정팀]') && 
                               !desc.includes(homeTeam) && !desc.includes(awayTeam);
              return isTeamStat || isNeutral;
            }).map((stat: any, idx: number) => (
              <div key={idx}>
                {renderParsedTable(stat, stat.description || '상대전적 통계')}
              </div>
            ))}
          </div>
        )}

        {/* Season Player Rankings */}
        {seasonPlayerRankings && (seasonPlayerRankings.home_team || seasonPlayerRankings.away_team) && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>시즌 선수 순위</h4>
            {(() => {
              const rankings = selectedTeam === 'home' 
                ? seasonPlayerRankings.home_team
                : seasonPlayerRankings.away_team;
              
              if (!rankings || rankings.length === 0) {
                return <p style={{color: '#6c757d'}}>선수 순위 데이터가 없음.</p>;
              }

              // 랭킹은 현재 헤더와 데이터가 있는 테이블 형식
              return rankings.map((ranking: any, idx: number) => (
                <div key={idx}>
                  {renderParsedTable(ranking, ranking.description || `시즌 순위 ${idx + 1}`)}
                </div>
              ));
            })()}
          </div>
        )}

        {/* No Data Message */}
        {!teamData.pitcher && !teamData.seasonStats && !teamData.recentPitcher && 
         !teamData.monthlyStats && !teamData.teamRecent && 
         teamData.threeSeasonStats.length === 0 && teamData.currentSeason.length === 0 && 
         teamData.headToHead.length === 0 && 
         (!seasonPlayerRankings || 
          (selectedTeam === 'home' ? 
            !seasonPlayerRankings.home_team || seasonPlayerRankings.home_team.length === 0 : 
            !seasonPlayerRankings.away_team || seasonPlayerRankings.away_team.length === 0)) && (
          <div style={styles.noData}>
            <p>{teamData.teamName}의 상세 데이터가 없음.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxHeight: '60vh',
    padding: spacing[6],
    backgroundColor: colors.white,
    overflow: 'auto' as const,
  },
  matchInfoHeader: {
    marginBottom: spacing[7],
    paddingBottom: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  matchTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[3],
  },
  matchDetails: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  teamButtons: {
    display: 'flex',
    marginBottom: spacing[7],
  },
  teamButton: {
    flex: 1,
    padding: spacing[4],
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectedTeamButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  content: {
    minHeight: '400px',
  },
  teamNameTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[6],
    color: colors.black,
    paddingLeft: spacing[3],
    borderLeft: `3px solid ${colors.black}`,
  },
  section: {
    marginBottom: spacing[10],
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[6],
    color: colors.gray[800],
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: spacing[3],
  },
  pitcherGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing[6],
  },
  tableContainer: {
    marginBottom: spacing[6],
  },
  tableTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[3],
    color: colors.gray[600],
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: spacing[4],
    backgroundColor: colors.gray[100],
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  },
  td: {
    padding: `${spacing[3]} ${spacing[4]}`,
    borderTop: 'none',
    borderLeft: 'none',
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  },
  noData: {
    textAlign: 'center' as const,
    padding: spacing[10],
    color: colors.gray[500],
    fontSize: typography.fontSize.md,
    backgroundColor: colors.gray[50],
  },
  pitcherSection: {
    marginBottom: spacing[6],
  },
  pitcherName: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[5],
    margin: `0 0 ${spacing[5]} 0`,
  },
};

export default BaseballDataDisplay;